import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { leafHead } from "@/lib/seo/leaf-head";
import { FaqBlock, ResultCard, ReservedResultsArea, SubHubGrid, buildBreadcrumbLd } from "@/components/age-calculator/shared";
import { TripleDateInput, AnalyzeButton, AnalyzingOverlay, useAnalyzeGate, todayISO } from "@/components/age-calculator/premium";
import { AnchorToc, AnchorSection, LifeProgressBar, WeekdayMilestoneGrid, buildAppFaqLd, chronoDiff, CopyResultButton } from "@/components/age-calculator/sub-shell";

const TITLE = "Military Bharti Age Calculator: Agniveer, Army & PMA Long Course";
const DESC = "Check age eligibility for Indian Agniveer (17.5-21), Army SSC (19-25), PMA Long Course (16-22) and Pakistan Army recruitment with an interactive DOB-driven checker.";

const FAQS = [
  { q: "What is the Agniveer age limit 2026?", a: "For Indian Army Agniveer 2026, candidates must be 17.5 to 21 years on the notification date. Age is not relaxed for any category under the Agnipath scheme." },
  { q: "What is the age limit for PMA Long Course in Pakistan?", a: "PMA Long Course accepts candidates 16 to 22 years for graduation entry; serving soldiers get a relaxation up to 23 years." },
  { q: "Which post has the highest upper age limit?", a: "Army Medical Corps SSC (Doctors) extends to 45 years for specialists. Standard combat SSC officer entry caps at 25 years." },
  { q: "How is bharti age counted — on notification or test day?", a: "It is counted on the cut-off date printed in the notification. Even one day over the upper band on that date makes you ineligible." },
];

const TOC = [
  { id: "calculator", label: "Calculator" },
  { id: "posts", label: "Post-wise Bands" },
  { id: "fitness", label: "Physical Standards" },
  { id: "faqs", label: "FAQs" },
];

type Post = { key: string; label: string; min: number; max: number };
const POSTS: Post[] = [
  { key: "agniveer", label: "🇮🇳 Agniveer (Army/Navy/AF)", min: 17.5, max: 21 },
  { key: "army-ssc", label: "🇮🇳 Army SSC Officer", min: 19, max: 25 },
  { key: "nda", label: "🇮🇳 NDA Entry", min: 16.5, max: 19.5 },
  { key: "pma-long", label: "🇵🇰 PMA Long Course", min: 16, max: 22 },
  { key: "pma-ssc", label: "🇵🇰 PMA Short Service", min: 21, max: 27 },
  { key: "pk-sepoy", label: "🇵🇰 Pakistan Army Sepoy", min: 17, max: 23 },
];

export const Route = createFileRoute("/age-calculator/military-bharti")({
  head: () => leafHead({
    path: "/age-calculator/military-bharti", title: TITLE, description: DESC,
    scripts: [
      buildAppFaqLd({ name: "Military Bharti Age Calculator", description: DESC, faqs: FAQS }),
      buildBreadcrumbLd([
        { name: "Home", url: "https://drhint.com/" },
        { name: "Age Calculator", url: "https://drhint.com/age-calculator" },
        { name: "Military Bharti", url: "https://drhint.com/age-calculator/military-bharti" },
      ]),
    ],
  }),
  component: Page,
});

function Page() {
  const [dob, setDob] = useState("");
  const [cutoff, setCutoff] = useState(todayISO());
  const [postKey, setPostKey] = useState("agniveer");
  const { analyzing, revealed, trigger, reset } = useAnalyzeGate();
  const post = POSTS.find((p) => p.key === postKey)!;
  const r = useMemo(() => (dob ? chronoDiff(dob, cutoff) : null), [dob, cutoff]);
  const ageDec = r ? r.years + r.months / 12 + r.days / 365 : 0;
  const eligible = r ? ageDec >= post.min && ageDec <= post.max : false;
  const copyText = r ? `${post.label} — age on ${cutoff}: ${r.years}y ${r.months}m — ${eligible ? "ELIGIBLE" : "NOT IN BAND"}` : "";

  return (
    <Layout>
      <section className="mx-auto max-w-5xl px-4 pt-10 pb-4">
        <nav className="text-xs text-muted-foreground mb-3">
          <Link to="/age-calculator" className="hover:text-primary">Age Calculator</Link> / Military Bharti
        </nav>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight">{TITLE}</h1>
        <p className="mt-3 text-muted-foreground max-w-3xl">
          Indian aur Pakistani armed-forces recruitment ke liye <strong>DOB-driven age band checker</strong>. Agniveer, NDA, PMA aur SSC entries covered. Wider date arithmetic ke liye <Link to="/age-calculator" className="text-primary underline">main hub</Link>.
        </p>
      </section>
      <AnchorToc items={TOC} />
      <section id="calculator" className="mx-auto max-w-5xl px-4 mt-4 scroll-mt-24">
        <div className="rounded-3xl border border-border bg-card p-6 shadow-sm">
          <div className="grid gap-5 sm:grid-cols-2">
            <TripleDateInput label="Date of Birth" value={dob} onChange={(v) => { setDob(v); reset(); }} max={todayISO()} />
            <TripleDateInput label="Notification Cut-off Date" value={cutoff} onChange={(v) => { setCutoff(v); reset(); }} quickChips />
          </div>
          <div className="mt-4">
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">Choose recruitment</div>
            <div className="flex flex-wrap gap-1.5">
              {POSTS.map((p) => (
                <button key={p.key} type="button" onClick={() => { setPostKey(p.key); reset(); }} className={`px-3 py-1.5 rounded-full text-xs font-bold transition ${postKey === p.key ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-muted/70"}`}>{p.label}</button>
              ))}
            </div>
          </div>
          <AnalyzeButton onClick={trigger} disabled={!dob || analyzing} label="Check Bharti Eligibility" />
          <AnalyzingOverlay visible={analyzing} />
          <ReservedResultsArea minHeight={260}>
            {revealed && r ? (
              <>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <ResultCard label={`Age on ${cutoff}`} value={`${r.years}y ${r.months}m`} />
                  <ResultCard label="Post" value={post.label} sub={`Band ${post.min}-${post.max}`} />
                  <ResultCard label="Decimal Age" value={ageDec.toFixed(2)} />
                  <ResultCard label="Status" value={eligible ? "ELIGIBLE ✅" : "OUT OF BAND ❌"} />
                </div>
                <div className="mt-4 flex justify-center"><CopyResultButton text={copyText} label="Copy Result" /></div>
                <LifeProgressBar dob={dob} expectancy={72} />
                <WeekdayMilestoneGrid dob={dob} />
              </>
            ) : !analyzing ? (
              <div className="mt-6 rounded-2xl border border-dashed border-border/70 p-8 text-center text-sm text-muted-foreground">Choose a post and enter DOB.</div>
            ) : null}
          </ReservedResultsArea>
        </div>
      </section>

      <AnchorSection id="posts" title="Bharti Age Bands — Full Table">
        <table>
          <thead><tr><th>Force / Entry</th><th>Min</th><th>Max</th></tr></thead>
          <tbody>{POSTS.map((p) => (<tr key={p.key}><td>{p.label}</td><td>{p.min}</td><td>{p.max}</td></tr>))}</tbody>
        </table>
      </AnchorSection>

      <AnchorSection id="fitness" title="Beyond Age — Physical Standards Snapshot">
        <ul>
          <li><strong>Height (male):</strong> 170 cm Indian Army General, 165 cm Pakistan Army General.</li>
          <li><strong>Chest expansion:</strong> minimum 5 cm.</li>
          <li><strong>Running:</strong> 1.6 km in under 5:30 (Group 1 — Indian Army Agniveer).</li>
          <li><strong>Vision:</strong> 6/6 unaided in the better eye, 6/9 worse eye.</li>
        </ul>
      </AnchorSection>

      <FaqBlock faqs={FAQS} />
      <SubHubGrid />
    </Layout>
  );
}